import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS, { getBackendUrl } from '../config/api';
import KuutioCubeGradient from '../components/KuutioCubeGradient';
import ModalWrapper from '../components/ModalWrapper';
import { useTranslation } from '../hooks/useTranslation.jsx';
import OtpLoginCard from '../components/auth/OtpLoginCard';
import { markPinSession } from '../utils/pinSession';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(180deg, 
    #000000 0%, 
    #000000 30%, 
    #1a0f2e 70%, 
    #2d1b69 90%,
    #4a2882 100%
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LogoImage = styled.img`
  width: 280px;
  height: 280px;
  object-fit: contain;
`;

const RotatingCubeObject = styled.object`
  width: 280px;
  height: 280px;
`;

/* COMMENTED OUT - Keep for later use
const TypewriterContainer = styled.div`
  margin-bottom: 64px;
  text-align: center;
  max-width: 600px;
`;

const TypewriterLine = styled.div`
  font-size: 24px;
  font-weight: ${props => props.title ? '700' : '400'};
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
  min-height: 43px;
  font-family: 'Courier New', monospace;

  ${props => props.showCursor && `
    &::after {
      content: '|';
      animation: blink 1s step-end infinite;
    }

    @keyframes blink {
      50% { opacity: 0; }
    }
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 64px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 24px;
`;

const WhatIsLink = styled.a`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const RoleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => props.primary ? '#4CAF50' : '#2196F3'};
  border-radius: 12px;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.primary ?
      'rgba(76, 175, 80, 0.1)' :
      'rgba(33, 150, 243, 0.1)'};
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonEmoji = styled.span`
  font-size: 24px;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const TestLink = styled.a`
  display: block;
  margin-top: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.25);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const NetworkInfo = styled.div`
  position: absolute;
  top: 32px;
  right: 32px;
  text-align: right;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
`;

const DebugInfo = styled.div`
  position: absolute;
  bottom: 100px;
  left: 32px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-family: 'Courier New', monospace;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const DebugLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 2px solid rgba(138, 43, 226, 0.3);
  border-radius: 16px;
  padding: 48px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 24px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const ModalText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const ContactLink = styled.a`
  display: inline-block;
  margin-top: 32px;
  padding: 12px 24px;
  background: rgba(138, 43, 226, 0.2);
  border: 2px solid rgba(138, 43, 226, 0.5);
  border-radius: 8px;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(138, 43, 226, 0.3);
    border-color: rgba(138, 43, 226, 0.8);
    transform: translateY(-2px);
  }
`;
// END OF COMMENTED OUT STYLED COMPONENTS */

// CRT-era look: monospace, cyan phosphor, subtle glow.
const CRT_CYAN = '#5ff7ff';
const CRT_GLOW = '0 0 6px rgba(95, 247, 255, 0.55)';
const CRT_FONT = "'VT323', 'IBM Plex Mono', 'Courier New', ui-monospace, monospace";

const welcomeTitle = {
  marginTop: '28px',
  fontFamily: CRT_FONT,
  fontWeight: 400,
  fontSize: '26px',
  letterSpacing: '0.06em',
  color: CRT_CYAN,
  textShadow: CRT_GLOW,
  minHeight: '1.4em',
  textAlign: 'center',
};

const cursor = {
  display: 'inline-block',
  width: '8px',
  height: '0.9em',
  background: CRT_CYAN,
  boxShadow: CRT_GLOW,
  marginLeft: '4px',
  verticalAlign: 'text-bottom',
  animation: 'kuutio-cursor-blink 1s step-end infinite',
};

// Ghost button — same CRT cyan, transparent fill, glow on hover.
const signInButton = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 24px',
  marginTop: '32px',
  background: 'transparent',
  color: CRT_CYAN,
  fontFamily: CRT_FONT,
  fontWeight: 400,
  fontSize: '22px',
  letterSpacing: '0.08em',
  textDecoration: 'none',
  border: `1px solid ${CRT_CYAN}`,
  borderRadius: '2px',
  cursor: 'pointer',
  textShadow: CRT_GLOW,
  transition: 'background 0.15s, box-shadow 0.15s',
};

export default function RoleSelection() {
  const { t } = useTranslation();
  // return_to_origin tells the backend which frontend origin to land on after OAuth
  const ssoHref = `${API_ENDPOINTS.AUTH_ENTRA_LOGIN()}?return_to=/instances&return_to_origin=${encodeURIComponent(window.location.origin)}`;

  const [titleText, setTitleText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const title = t('landing.title');
    let i = 0;
    let timeoutId;
    const tick = () => {
      if (i <= title.length) {
        setTitleText(title.slice(0, i));
        i++;
        timeoutId = setTimeout(tick, 35);
      } else {
        timeoutId = setTimeout(() => setShowCursor(false), 1500);
      }
    };
    tick();
    return () => clearTimeout(timeoutId);
  }, [t]);

  return (
    <Container>
      <style>{`@keyframes kuutio-cursor-blink { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }`}</style>
      <KuutioCubeGradient size={400} />
      <h1 style={welcomeTitle}>
        {titleText}
        {showCursor && <span style={cursor} aria-hidden="true" />}
      </h1>
      <a
        href={ssoHref}
        style={signInButton}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(95, 247, 255, 0.08)';
          e.currentTarget.style.boxShadow = '0 0 14px rgba(95, 247, 255, 0.45)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        &gt; {t('landing.sitraUser')}
      </a>
      <OtpLoginCard />
    </Container>
  );
}

/* COMMENTED OUT - Keep for later use
function RoleSelectionFull() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setSelectedInstanceId } = useInstance();
  const [titleText, setTitleText] = useState('');
  const [subtitleText, setSubtitleText] = useState('');
  const [showTitleCursor, setShowTitleCursor] = useState(true);
  const [showSubtitleCursor, setShowSubtitleCursor] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  // Demo mode configuration
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';
  const DEMO_INSTANCE_ID = import.meta.env.VITE_DEFAULT_INSTANCE_ID || '54b6fb24-34bd-424f-bcf5-79ca8c4b3423';

  // Typewriter effect with two lines and pause between them
  useEffect(() => {
    const title = t('landing.title');
    const subtitle = t('landing.subtitle');
    let titleIndex = 0;
    let subtitleIndex = 0;
    let phase = 'title'; // 'title', 'pause', 'subtitle', 'done'
    let timeoutId;

    const typeNextCharacter = () => {
      if (phase === 'title') {
        if (titleIndex <= title.length) {
          setTitleText(title.slice(0, titleIndex));
          titleIndex++;
          timeoutId = setTimeout(typeNextCharacter, 25); // Fast 25ms delay
        } else {
          // Title complete, pause before second line
          phase = 'pause';
          setShowTitleCursor(false);
          timeoutId = setTimeout(() => {
            phase = 'subtitle';
            setShowSubtitleCursor(true);
            typeNextCharacter();
          }, 800); // 800ms pause between lines
        }
      } else if (phase === 'subtitle') {
        if (subtitleIndex <= subtitle.length) {
          setSubtitleText(subtitle.slice(0, subtitleIndex));
          subtitleIndex++;
          timeoutId = setTimeout(typeNextCharacter, 25); // Fast 25ms delay
        } else {
          // All done
          phase = 'done';
        }
      }
    };

    // Start typing
    typeNextCharacter();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [t]);

  const handleSitraUserSelect = () => {
    // Tier 1 admin auth: Sitra Entra ID SSO.
    // Backend handles the OIDC dance, sets a session cookie, redirects back
    // to FRONTEND_URL/instances. We hand off via top-level navigation
    // (not fetch) so Microsoft's redirects work without CORS gymnastics.
    const backendUrl = getBackendUrl();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${backendUrl}/api/auth/entra/login?return_to=/instances&return_to_origin=${origin}`;
  };

  const handleDisplaySelect = () => {
    // Show PIN modal instead of navigating away
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  const handlePinSubmit = async () => {
    if (!pin || pin.length < 4) {
      setPinError('Anna kelvollinen PIN-koodi');
      return;
    }

    if (isDemoMode) {
      // Demo mode: Any PIN accepted, use PIN as participant ID
      console.log('🎯 Demo mode: Accepting PIN', pin, 'and routing to welcome');
      setSelectedInstanceId(DEMO_INSTANCE_ID);

      // Store PIN as participant ID in localStorage for persistence
      localStorage.setItem('participantId', pin);

      setShowPinModal(false);
      // Route to welcome screen where user chooses scenario
      navigate('/welcome');
    } else {
      // Normal mode: Validate PIN against backend
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_PIN_LOGIN(), {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pinCode: pin })
        });

        const data = await response.json();

        if (data.success) {
          markPinSession();
          // Store session data
          localStorage.setItem('participantId', data.session.participantId);
          localStorage.setItem('selectedInstanceId', data.session.kuutioInstanceId);
          localStorage.setItem('sessionToken', data.session.token);
          localStorage.setItem('displayName', data.session.displayName || '');

          setSelectedInstanceId(data.session.kuutioInstanceId);
          setShowPinModal(false);
          navigate('/welcome');
        } else {
          // The API's message is for the log; the person sees it in the instance language
          console.warn('PIN login refused:', data.error);
          setPinError(t('pinLogin.invalidPin'));
        }
      } catch (err) {
        console.error('PIN validation error:', err);
        setPinError(t('pinLogin.connectionError'));
      }
    }
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setPin('');
    setPinError('');
  };

  // Get local IP for reference
  const getLocalIP = () => {
    const hostname = window.location.hostname;
    const port = window.location.port;
    return port ? `${hostname}:${port}` : hostname;
  };

  return (
    <Container>
      <NetworkInfo>
        <div>Network: {getLocalIP()}</div>
        <div>Session: default</div>
      </NetworkInfo>

      <LogoVideo
        src="/assets/kuutio-rotating.mp4"
        autoPlay
        loop
        muted
        playsInline
      />

      <TypewriterContainer>
        <TypewriterLine title showCursor={showTitleCursor}>
          {titleText}
        </TypewriterLine>
        <TypewriterLine showCursor={showSubtitleCursor}>
          {subtitleText}
        </TypewriterLine>
      </TypewriterContainer>

      <ButtonContainer>
        {isDemoMode ? (
          // Demo mode: Only show "Minulla on kutsu" button
          <RoleButton primary onClick={handleDisplaySelect}>
            <ButtonEmoji>🎫</ButtonEmoji>
            {t('landing.invited')}
          </RoleButton>
        ) : (
          // Normal mode: Show both buttons
          <ButtonRow>
            <RoleButton primary onClick={handleSitraUserSelect}>
              <ButtonEmoji>👔</ButtonEmoji>
              {t('landing.sitraUser')}
            </RoleButton>

            <RoleButton onClick={handleDisplaySelect}>
              <ButtonEmoji>🎫</ButtonEmoji>
              {t('landing.invited')}
            </RoleButton>
          </ButtonRow>
        )}

        {!isDemoMode && (
          <WhatIsLink onClick={() => setShowInfoModal(true)}>
            {t('landing.whatIsKuutio')}
          </WhatIsLink>
        )}
      </ButtonContainer>

      <DebugInfo>
        <Bug size={14} />
        <DebugLines>
          <div>ENV: {import.meta.env.MODE}</div>
          <div>SOCKET: {import.meta.env.VITE_SOCKET_URL || 'http://localhost:3010'}</div>
          <div>BACKEND: {import.meta.env.VITE_SOCKET_URL || 'http://localhost:3010'}</div>
          <div>RAG: {(import.meta.env.VITE_SOCKET_URL || 'http://localhost:3010').replace(':3010', ':3020')}</div>
          <div>DEMO: {import.meta.env.VITE_DEMO_MODE || 'false'}</div>
        </DebugLines>
      </DebugInfo>

      <Footer>
        <FooterText>
          {t('landing.footer')}
        </FooterText>
        <TestLink onClick={() => navigate('/testikuutio')}>
          Testikuutio
        </TestLink>
      </Footer>

      {showInfoModal && (
        <ModalWrapper visible={showInfoModal} onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <ModalTitle>{t('landing.infoModal.title')}</ModalTitle>
            <ModalText>{t('landing.infoModal.paragraph1')}</ModalText>
            <ModalText>{t('landing.infoModal.paragraph2')}</ModalText>
            <ModalText>{t('landing.infoModal.paragraph3')}</ModalText>
            <ContactLink href="mailto:kuutio@sitra.fi">
              {t('landing.infoModal.contactButton')}
            </ContactLink>
          </ModalContent>
        </ModalWrapper>
      )}

      {showPinModal && (
        <ModalWrapper visible={showPinModal} onClose={handlePinCancel} showCloseButton={false}>
          <ModalContent>
            <ModalTitle>{t('pinLogin.modalTitle')}</ModalTitle>
            <ModalText>{t('pinLogin.modalText')}</ModalText>
            <div style={{ marginTop: '24px' }}>
              <input
                type="text"
                value={pin}
                onChange={(e) => setPin(e.target.value.slice(0, 6).toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                placeholder="ABC123"
                maxLength={6}
                autoFocus
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  border: pinError ? '2px solid #ef4444' : '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: 'white',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
              />
              {pinError && (
                <div style={{ color: '#ef4444', marginTop: '12px', fontSize: '14px' }}>
                  {pinError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <RoleButton onClick={handlePinCancel} style={{ flex: 1 }}>
                {t('common.cancel')}
              </RoleButton>
              <RoleButton primary onClick={handlePinSubmit} style={{ flex: 1 }}>
                {t('common.continue')}
              </RoleButton>
            </div>
          </ModalContent>
        </ModalWrapper>
      )}
    </Container>
  );
}
// END OF COMMENTED OUT CODE */